import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { CustomPaginatorIntl } from '../../../generic/paginator/custom-paginator';
import { ResourceData } from '../../../../store/resourceDatabase/resource.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { EquipmentService } from '../../../../services/equipment/equipment.service';
import { HttpRequestService } from '../../../../utilities/http/request.utilities';
import { environment } from '../../../../../environments/environment';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { EquipmentRecordComponent } from '../equipment-record/equipment-record.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { jsPDF } from "jspdf";
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { FormControl } from '@angular/forms';
import { BulkUploadDialogComponent } from '../../../generic/bulk-upload/bulk-upload-dialog.component';

type FilterValues = {
  make: string[];
  model: string[];
};

@Component({
  selector: 'app-resource-database',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatCardModule, MatInputModule,
    MatTableModule, MatToolbarModule, MatSidenavModule, MatDatepickerModule, MatNativeDateModule, MatIcon, MatIconModule,
    MatPaginatorModule, MatOptionModule, MatFormFieldModule, MatSelectModule, MatSortModule, MatProgressSpinnerModule, NgxMatSelectSearchModule,
    ],
    providers: [
      { provide: MatPaginatorIntl, useClass: CustomPaginatorIntl }
    ],
  templateUrl: './resource-database.component.html',
  styleUrls: ['./resource-database.component.css']
})
export class ResourceDatabaseComponent implements OnInit {
  pvcMakes: string[] = []; //This will hold the pvc makes returned from the makes API
  displayedColumns: string[] = [
    'make',
    'model',
    'oec',
    'resources',
    'expandRecord'
  ];
  dataSource = new MatTableDataSource<ResourceData>();
  baseData: ResourceData[] = [];
  fromDate: Date | null = null;
  toDate: Date | null = null;
  isLoading: boolean = false;

  filters: FilterValues = {
    make: [],
    model: [],
  };

  makeFilterCtrl = new FormControl('');
  modelFilterCtrl = new FormControl('');
  uniqueMakes: string[] = [];
  uniqueModels: string[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;


  constructor(private httpRequestService: HttpRequestService, private dialog: MatDialog, private sharedData: EquipmentService) {}

  openBulkUploadDialog(): void {
    this.dialog.open(BulkUploadDialogComponent, {
      width: '600px',
      disableClose: true
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      await this.fetchMakes();
      if (this.filters.make.length > 0) {
        await this.loadResourceData();
      }
      this.applyFilters();
    } catch (error) {
      console.error('Failed to initialize component:', error);
    }
  }

  async onMakeChange(): Promise<void> {
    try {
      this.isLoading = true;
      await this.loadResourceData().finally(() => this.isLoading = false);
      this.applyFilters();
      this.updateUniqueValues();
    } catch (error) {
      console.error('Failed to load data on make change:', error);
    }
  }

  applyFilters(): void {
    const filteredData = this.baseData.filter(item => {
      this.sharedData.setNewValue(item.make); //This will take the selected make and set it in the shared variable
      const matchesMake = this.filters.make.length ? this.filters.make.includes(item.make) : true;
      const matchesModel = this.filters.model.length ? this.filters.model.includes(item.model) : true;

      return matchesMake && matchesModel;
    });

    this.dataSource.data = this.getUniqueMakeModelEntries(filteredData);
    this.updateUniqueValues();
    this.updateDisplayedColumns();
  }

  private updateUniqueValues(): void {
    const modelsFromMake = this.baseData
      .filter(item => this.filters.make.includes(item.make))
      .map(item => item.model);
    this.uniqueModels = [...new Set(modelsFromMake)];
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = this.displayedColumns.filter((col) => col !== 'pvc');
  }

  clearFilter(filterName: keyof FilterValues): void {
    this.filters[filterName] = [];
    this.applyFilters();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private async loadResourceData(): Promise<void> {
    if (!this.filters.make || this.filters.make.length === 0) {
      console.warn('No make selected for fetching resources.');
      this.baseData = [];
      this.dataSource.data = [];
      return;
    }

    const makeParam = this.filters.make.join(',');
    const endpoint = `${environment.integrations.adminResources}?make=${makeParam}`;

    return new Promise((resolve, reject) => {
      this.httpRequestService.request<any>('GET', endpoint).subscribe({
        next: (response) => {
          if (response && Array.isArray(response.items)) {
            this.baseData = response.items.map((item: any) => {
              const validManuals = (item.Manuals || []).filter((manual: any) => {
                return (
                  manual.url && 
                  manual.url !== null && 
                  manual.url !== "" &&  
                  manual.url.endsWith('.pdf') &&
                  !['Manual not available for Model', 'Manual not available for SN'].includes(manual.url)
                );
              });
              
              return {
              make: item.MAKE,
              model: item.Models,
              endSerial: item.EndSerial,
              startSerial: item.StartSerial,
              model_start_end: item.MODEL_START_END,
              resources: validManuals.length || 0,
              pdf_link: validManuals,
              pvc: item.PVC || 'N/A',
              prompts: Array.isArray(item.PROMPTS?.resources) ? item.PROMPTS.resources.map((prompt: any) => prompt.prompt) : []
            };
          }); 
            this.dataSource.data = this.baseData;

          } else {
            console.warn('Unexpected response format for resources:', response);
            this.baseData = [];
            this.dataSource.data = [];
          }
          resolve();
        },
        error: (error) => {
          console.error('Error fetching resources:', error);
          this.baseData = [];
          this.dataSource.data = [];
          reject(error);
        }
      });
    });
  }

  private async fetchMakes(): Promise<void> {
    const urlParams = new URLSearchParams();
    const endpoint = environment.integrations.adminMake;

    return new Promise((resolve, reject) => {
      this.httpRequestService.request<any>('GET', `${endpoint}?${urlParams}`).subscribe({
        next: (response) => {
          if (response && Array.isArray(response.makes)) {
            this.pvcMakes = response.pvc_makes; //Reads the pvc object from make API call
            this.sharedData.setValue(this.pvcMakes); //Sets it in the shared variable
            this.uniqueMakes = response.makes;
          } else {
            console.warn('Unexpected response format for makes:', response);
            this.uniqueMakes = [];
          }
          resolve();
        },
        error: (error) => {
          console.error('Error fetching makes:', error);
          reject(error);
        }
      });
    });
  }

  openEquipmentRecordModal(element: any): void {
    const relatedItems = this.baseData.filter(item =>
      item.make === element.make && item.model === element.model
    );
    const dialogRef = this.dialog.open(EquipmentRecordComponent, {
      data: {
        ...element,
        relatedCombinations: relatedItems,
         source: 'resource-database',
      },
      width: '300px',
      height: '800px',
    });

    dialogRef.afterClosed().subscribe(() => {
      this.handleModalResult();
    });
  }

  async handleModalResult(): Promise<void> {
      await this.onMakeChange();
  }
  getFilteredMakes(): string[] {
    const filterValue = this.makeFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueMakes.filter(make => make.toLowerCase().includes(filterValue));
  }
  
  getFilteredModels(): string[] {
    const filterValue = this.modelFilterCtrl.value?.toLowerCase() || '';
    return this.uniqueModels.filter(model => model.toLowerCase().includes(filterValue));
  }

  exportToCSV(): void {
    if (!this.dataSource?.data?.length) {
      console.warn('No data available to export.');
      return;
    }
    const columnMap: { [key: string]: (item: ResourceData) => string } = {
      make: (item) => item.make,
      model: (item) => item.model,
      oec: (item) => item.oec || '',
      resources: (item) => item.resources.toString(),
      resourceTypes: (item) => {
        const titles = (item.pdf_link || []).map(link => link.title);
        const uniqueTitles = Array.from(new Set(titles));
        return uniqueTitles.join(' / ');
      },
      prompts: (item) => (item.prompts || []).join(' | ')
    };
    const exportColumns = this.displayedColumns
      .filter(col => col !== 'expandRecord')
      .flatMap(col => col === 'resources' ? ['resources', 'resourceTypes'] : [col]);
    if (!exportColumns.includes('prompts')) {
      exportColumns.push('prompts');
    }
    const headers = exportColumns.map(col => col.toUpperCase());
    const rows = this.dataSource.data.map(item =>
      exportColumns.map(col => {
        const valueGetter = columnMap[col];
        let value = valueGetter ? valueGetter(item) : '';
        value = this.normalizeText(value);
        value = value.replace(/"/g, '""').replace(/\r?\n|\r/g, ' ');
        return value;
      })
    );
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'resource_database.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  normalizeText(text: string): string {
    return text
      .normalize('NFKD')
      .replace(/[’‘‛]/g, "'")
      .replace(/[“”„]/g, '"')
      .replace(/–|—/g, '-')
      .replace(/[^\x00-\x7F]/g, '');
  }

  exportToPDF(): void {
    if (!this.dataSource?.data?.length) {
      console.warn('No data available to export.');
      return;
    }
    const pdf = new jsPDF();
    pdf.setFontSize(16);
    pdf.text('Resource Database Export', 10, 10);
    pdf.setFontSize(12);
    let yPos = 20;
    const lineSpacing = 10;
    const maxHeight = 280;
    const maxWidth = 190;

    this.dataSource.data.forEach((item, index) => {
      const titles = (item.pdf_link || []).map(link => link.title);
      const uniqueTitles = Array.from(new Set(titles));
      const titleText = `Resources Type: ${uniqueTitles.join(' / ')}`;
      const promptText = `Prompts: ${item.prompts.join('; ')}`;
      const splitPrompt = pdf.splitTextToSize(promptText, maxWidth);
      const splitTitle = pdf.splitTextToSize(titleText, maxWidth);
      const baseLines = 5; // Make, Model, PVC, Resources, Entry
      const oecLine = item.oec ? 1 : 0;
      const estimatedLines = baseLines + oecLine + splitTitle.length + splitPrompt.length + 1;
      const blockHeight = estimatedLines * lineSpacing;
      if (yPos + blockHeight > maxHeight) {
        pdf.addPage();
        yPos = 20;
      }
      pdf.text(`Entry ${index + 1}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Make: ${item.make}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Model: ${item.model}`, 10, yPos); yPos += lineSpacing;
      if (item.oec) {
        pdf.text(`OEC: ${item.oec}`, 10, yPos); yPos += lineSpacing;
      }
      pdf.text(`PVC: ${item.pvc}`, 10, yPos); yPos += lineSpacing;
      pdf.text(`Resources: ${item.resources}`, 10, yPos); yPos += lineSpacing;
      splitTitle.forEach((line: string) => {
        pdf.text(line, 10, yPos);
        yPos += lineSpacing;
      });

      splitPrompt.forEach((line: string) => {
        if (yPos + lineSpacing > maxHeight) {
          pdf.addPage();
          yPos = 20;
        }
        pdf.text(line, 10, yPos);
        yPos += lineSpacing;
      });
      yPos += lineSpacing;
    });
    pdf.save('resource_database.pdf');
  }

  clearAllFilters(): void {
    this.filters = {
      make: this.baseData.map(item => item.make),
      model: [],
    };
    this.applyFilters();
  }

  private getUniqueMakeModelEntries(data: ResourceData[]): ResourceData[] {
    const grouped = new Map<string, ResourceData>();
    data.forEach(item => {
      const key = `${item.make}-${item.model}`;
      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, {
          ...item,
          pdf_link: [...(item.pdf_link || [])],
          prompts: [...(item.prompts || [])]
        });
      } else {
        const combinedLinks = [...(existing.pdf_link || []), ...(item.pdf_link || [])];
        const seenUrls = new Set<string>();
        existing.pdf_link = combinedLinks.filter(link => {
          const isNew = !seenUrls.has(link.url);
          seenUrls.add(link.url);
          return isNew;
        });
        existing.prompts = Array.from(new Set([
          ...(existing.prompts || []),
          ...(item.prompts || [])
        ]));
      }
    });
    return Array.from(grouped.values()).map(item => {
      const uniqueUrls = new Set(item.pdf_link?.map(link => link.url));
      return {
        ...item,
        resources: uniqueUrls.size
      };
    });
  }

}